import React, { useMemo } from 'react'

import { Grid } from '@mui/material'
import {
  AutocompleteInput,
  BooleanInput,
  Edit,
  EditActionsProps,
  FormDataConsumer,
  FormTab,
  ReferenceInput,
  SaveButton,
  SelectInput,
  ShowButton,
  TabbedForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useGetList,
  useNotify,
  useRedirect,
  useRefresh,
  useTranslate,
} from 'react-admin'
import { useParams } from 'react-router-dom'

import {
  LXEditPeriodicRates,
  LXEditPeriodicRatesHandle,
} from '../../components/LXEditPeriodicRates'
import { NoDeleteEditToolbar } from '../../components/NoDeleteEditToolbar'
import { ProductsHelper } from '../../helpers/Products.helper'
import { useUserStore } from '../../stores/useUserStore'
import { ProductType } from '../../types/ProductType'
import { Forbidden } from '../Forbidden'
import { LXField } from './../../components/LXField'
import { LXImageInput } from './../../components/LXImageInput'
import { ListHelper } from './../../helpers/List.helper'
import styles from './Beneficiary.module.scss'
import { BeneficiaryEditStatementSettings } from './BeneficiaryEdit/BeneficiaryEditStatementsSettings'

export const BeneficiaryEdit = (): JSX.Element => {
  const translate = useTranslate()
  const refresh = useRefresh()
  const redirect = useRedirect()
  const params = useParams()
  const notify = useNotify()
  const userStore = useUserStore()

  const editRateRef = React.useRef<LXEditPeriodicRatesHandle>(null)

  const onSuccess = React.useCallback(async () => {
    await editRateRef.current?.save()
    refresh()
    redirect('show', '/beneficiaries', params.id)
  }, [refresh, redirect, params.id])

  return (
    <Edit
      className={styles.edit}
      actions={<BeneficiaryEditActions />}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess,
        onError: (error: any, context: any) => {
          const k = error.body.key

          notify(`${k ? translate(`errors.${k}`) : error.message} - ${context.data.code}`, {
            type: 'error',
          })
        },
      }}
      redirect="show"
    >
      <TabbedForm toolbar={<NoDeleteEditToolbar />}>
        <FormTab label={translate('pages.beneficiaries._tabs.1')} contentClassName={styles.tab}>
          <BeneficiaryEditInformations />
        </FormTab>
        <FormTab
          label={translate('pages.beneficiaries._tabs.3')}
          path="periodic-rates"
          contentClassName={styles.tab}
        >
          {userStore.isAuthorised('beneficiary_rates.edit') ? (
            <LXEditPeriodicRates
              ref={editRateRef}
              rateable="beneficiary"
              rateableId={params.id as string}
            />
          ) : (
            <Forbidden permission="beneficiary_rates.edit" />
          )}
        </FormTab>
        <FormTab
          label={translate('pages.beneficiaries.statementSettings.title')}
          path="statement-settings"
          contentClassName={styles.tab}
        >
          {userStore.isAuthorised('statements.edit') ? (
            <BeneficiaryEditStatementSettings />
          ) : (
            <Forbidden permission="statements.edit" />
          )}
        </FormTab>
      </TabbedForm>
    </Edit>
  )
}

const BeneficiaryEditInformations = () => {
  const translate = useTranslate()
  const userStore = useUserStore()

  const { data } = useGetList<ProductType>('productTypes', {
    filter: {},
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  })

  const productTypes = useMemo(
    () => ProductsHelper.formatProductTypes(data ?? [], translate),
    [data, translate],
  )

  return (
    <Grid container direction="row" spacing={3}>
      <LXImageInput
        source="logoBase64"
        label={translate('_generics.logo')}
        placeholder={translate('pages.beneficiaries.imagePlaceholder')}
      />
      <Grid item container direction="row" className={styles.edit} lg={9} spacing={2}>
        <LXField source="name" label={translate('_generics.name')} elementType={TextInput} />
        <LXField>
          <TextInput
            source="code"
            label={translate('_generics.code')}
            inputMode="text"
            inputProps={{
              maxLength: 3,
            }}
          />
        </LXField>
        <LXField>
          <SelectInput
            source="cms"
            fullWidth
            choices={userStore.getCMSList()}
            defaultValue="logicomix"
            label={translate('pages.youtubeChannels.cms')}
          />
        </LXField>
        <LXField
          source="contactEmail"
          label={translate('pages.beneficiaries.contactEmail')}
          elementType={TextInput}
        />
        <LXField source="siret" elementType={TextInput} />
        <LXField source="tvaNumber" elementType={TextInput} />
        <LXField>
          <ReferenceInput
            source="beneficiaryTypeId"
            reference="beneficiaryTypes"
            fullWidth
            sort={ListHelper.DEFAULT_SORT}
          >
            <AutocompleteInput
              optionText="name"
              label={translate('pages.beneficiaries.beneficiaryType')}
              isRequired
            />
          </ReferenceInput>
        </LXField>
      </Grid>
      <Grid item container direction="row" className={styles.edit} lg={12} spacing={2}>
        <Grid item container direction="row" className={styles.edit} lg={2} />
        <LXField>
          <SelectInput
            source="dearPoliteForm"
            fullWidth
            choices={[
              {
                id: translate('pages.beneficiaries.dearPolite.options.single_male'),
                name: translate('pages.beneficiaries.dearPolite.options.single_male'),
              },
              {
                id: translate('pages.beneficiaries.dearPolite.options.single_female'),
                name: translate('pages.beneficiaries.dearPolite.options.single_female'),
              },
              {
                id: translate('pages.beneficiaries.dearPolite.options.plural_male'),
                name: translate('pages.beneficiaries.dearPolite.options.plural_male'),
              },
              {
                id: translate('pages.beneficiaries.dearPolite.options.plural_female'),
                name: translate('pages.beneficiaries.dearPolite.options.plural_female'),
              },
            ]}
            label={translate('pages.beneficiaries.dearPolite.title')}
          />
        </LXField>
        <LXField
          source="contactFirstName"
          label={translate('pages.beneficiaries.contactFirstName')}
          elementType={TextInput}
        />
        <LXField
          source="contactName"
          label={translate('pages.beneficiaries.contactName')}
          elementType={TextInput}
        />
      </Grid>
      <Grid item container direction="row" className={styles.edit} lg={12} spacing={2}>
        <Grid item container direction="row" className={styles.edit} lg={2} />
        <LXField source="address" label={translate('_generics.address')} elementType={TextInput} />
        <LXField
          source="address2"
          label={translate('_generics.address2')}
          elementType={TextInput}
        />
      </Grid>
      <Grid item container direction="row" className={styles.edit} lg={10}>
        <Grid item container direction="row" className={styles.edit} lg={2} />
        <BooleanInput
          source="isAutoCorrected"
          label={translate('pages.beneficiaries.isAutoCorrected')}
        />
        <LXField>
          <ReferenceInput
            source="defaultDatavizPresetId"
            reference="datavizPresets"
            fullWidth
            sort={ListHelper.DEFAULT_SORT}
          >
            <AutocompleteInput
              optionText="name"
              label={translate('pages.beneficiaries.defaultDatavizPreset')}
            />
          </ReferenceInput>
        </LXField>
        <Grid item container direction="row" className={styles.edit} lg={6}>
          <FormDataConsumer>
            {({ formData }) =>
              formData.isAutoCorrected && (
                <>
                  <LXField size={12}>
                    <ReferenceInput
                      allowEmpty
                      className={styles.autocomplete}
                      source="defaultProductId"
                      filter={{ beneficiaryId: formData.id }}
                      reference="products"
                      sort={ListHelper.DEFAULT_SORT}
                    >
                      <AutocompleteInput
                        optionText="name"
                        className={styles.autocomplete}
                        helperText={false}
                        label={translate('pages.beneficiaries.defaultProductId')}
                      />
                    </ReferenceInput>
                  </LXField>
                  <LXField size={12}>
                    <AutocompleteInput
                      className={styles.autocomplete}
                      key="productType"
                      source="defaultProductTypeId"
                      label="pages.beneficiaries.defaultProductTypeId"
                      choices={productTypes}
                      groupBy={option => option.group}
                      helperText={false}
                      alwaysOn
                    />
                  </LXField>
                </>
              )
            }
          </FormDataConsumer>
        </Grid>
      </Grid>
      <Grid item container direction="row" className={styles.edit} lg={12} spacing={2}>
        <Grid item container direction="row" className={styles.edit} lg={2} />
        <LXField
          source="mediaUploadGoogleDriveFolderId"
          label={translate('pages.beneficiaries.mediaUploadGoogleDriveFolderId')}
          elementType={TextInput}
        />
      </Grid>
    </Grid>
  )
}

const BeneficiaryEditActions = ({ data }: EditActionsProps) => (
  <TopToolbar>
    <ShowButton record={data} />
  </TopToolbar>
)
